// Central content config for the 2027 Reunion "Grand Reveal" page (/reunion-2027).
// Edit THIS FILE to drop in real YouTube links, external URLs, map pins, and final
// cost copy when the reveal goes live. Every value marked `TODO` is a placeholder.

import { formatCurrency, formatDate } from '../utils/format';
import type { Reunion } from '../types/reunion';

const IMG = '/images/reunion-2027';

export interface RevealLink {
  label: string;
  url: string;
  description: string;
}

export interface RevealPin {
  lat: number;
  lng: number;
  label: string;
}

export interface RevealGalleryItem {
  photo: string;
  caption: string;
}

export interface RevealAccommodation {
  photo: string;
  title: string;
  blurb: string;
  tourUrl?: string;
}

// Matterport self-guided 3D walkthroughs of the actual Evermore unit/areas.
// All scenes live on one hosted tour; the `?m=` + camera params target each view.
const TOUR_BASE = 'https://selfwalkevermoreresortorlando.web.app/';
export const tourLinks = {
  streetView: `${TOUR_BASE}?m=fvT5RTBvUoY&sr=2.45,-1.33&ss=211`,
  backyardPool: `${TOUR_BASE}?m=fvT5RTBvUoY&sr=-2.76,-1.18&ss=6`,
  kitchen: `${TOUR_BASE}?m=fvT5RTBvUoY&sr=-.18,-.47&ss=52`,
  floor1: `${TOUR_BASE}?m=fvT5RTBvUoY&sr=-.19,.08&ss=71`,
  floor2: `${TOUR_BASE}?m=fvT5RTBvUoY&sr=-2.72,-1.26&ss=136`,
  floor3: `${TOUR_BASE}?m=fvT5RTBvUoY&sr=-2.71,-1.15&ss=197`,
  beach: `${TOUR_BASE}?m=ta2wjUw69sX&sr=-.03,.54&ss=187`,
} as const;

export interface RevealStat {
  value: number;
  suffix?: string;
  label: string;
}

export interface RevealFloor {
  id: string;
  label: string;
  plan: string;
  blurb: string;
  tourUrl?: string;
}

export interface RevealSection {
  id: string;
  label: string;
}

export interface RevealCtaTarget {
  label: string;
  to: string;
}

// Single source of truth for in-page anchor ids + SectionNav links.
// The `id` values MUST match the section root ids rendered on the page.
export const sections: RevealSection[] = [
  { id: 'hero', label: 'Welcome' },
  { id: 'accommodations', label: 'Stay' },
  { id: 'residence', label: 'The House' },
  { id: 'cost', label: 'Cost' },
  { id: 'why-here', label: 'Why Here' },
  { id: 'gallery', label: 'Gallery' },
];

export const reunion2027 = {
  hero: {
    eyebrow: 'The 2027 Goldson-Wade Family Reunion',
    headline: "We're going to Evermore Resort",
    destination: 'Orlando, Florida',
    subhead:
      'Three days together on a 20-acre private beach lagoon, minutes from Walt Disney World. This is the one we plan around.',
    bg: `${IMG}/evermore_bay_aerial_1920x1080.jpg.webp`,
    eventDate: '2027-07-29', // Thursday, Jul 29 2027 (3-night stay through Sun Aug 1)
    dateLabel: 'July 29–August 1, 2027',
  },

  // Auth-aware primary action used by the hero and the sticky SectionNav.
  cta: {
    signedOut: { label: 'Register', to: '/register' } as RevealCtaTarget,
    signedIn: { label: 'Manage your registration', to: '/dashboard' } as RevealCtaTarget,
  },

  accommodations: {
    intro:
      'Everyone under a few roofs — private chef-grade kitchens, heated pools, game rooms, and beds for all of us. No scattered hotel rooms; the whole family, together.',
    items: [
      {
        photo: `${IMG}/aerial_houses_1920x1080.jpg.webp`,
        title: 'Two 11-Bedroom Houses',
        blurb:
          'Each house sleeps up to 32 — 11 bedrooms, 13 baths, private heated saltwater pool, gourmet kitchen, indoor slide, arcade, and a backyard lanai with a gas grill.',
        tourUrl: tourLinks.streetView,
      },
      {
        photo: `${IMG}/houses_backyard_private_pool_1920x1080.jpg.webp`,
        title: 'Private Pools & Lanai',
        blurb:
          'Your own heated pool steps from the back door — morning swims, evening cookouts, and the kids never far from sight.',
        tourUrl: tourLinks.backyardPool,
      },
      {
        photo: `${IMG}/3_story_house_kitchen_1920x1080.jpg.webp`,
        title: 'Gourmet Kitchens',
        blurb:
          'Two fridges, two dishwashers, and room for the whole crew to cook together — grill nights, big breakfasts, and Sunday-morning coffee for everyone.',
        tourUrl: tourLinks.kitchen,
      },
    ] as RevealAccommodation[],
  },

  // The marquee 11-bedroom residence — floor plans extracted from the resort PDF.
  residence: {
    eyebrow: 'The Residence',
    title: 'Two 11-bedroom mansions, all to ourselves',
    subtitle:
      "Not a row of hotel rooms — two private 7,800 sq ft homes (plus resort flats as needed), each with three floors and room for 32. Space for the whole family to spread out and still be together. Here's the actual floor plan of one.",
    // Neighborhood aerial that floats in and zooms toward the house as you scroll.
    exteriorWide: `${IMG}/aerial_houses_1920x1080.jpg.webp`,
    exteriorClose: `${IMG}/houses_backyard_private_pool_1920x1080.jpg.webp`,
    stats: [
      { value: 11, label: 'Bedrooms' },
      { value: 32, label: 'Sleeps' },
      { value: 19, label: 'Beds' },
      { value: 13, label: 'Baths' },
      { value: 7818, suffix: ' sqft', label: 'Square Feet' },
    ] as RevealStat[],
    floors: [
      {
        id: 'ground',
        label: 'Ground Floor',
        plan: `${IMG}/floorplans/11br-ground.webp`,
        blurb:
          'The heart of the house — a great room, two long dining tables, a gourmet kitchen with a chef island, and the first run of bedrooms. Where everyone lands.',
        tourUrl: tourLinks.floor1,
      },
      {
        id: 'second',
        label: 'Second Floor',
        plan: `${IMG}/floorplans/11br-second.webp`,
        blurb:
          'A second living lounge surrounded by the bulk of the bedrooms and en-suite baths — quiet corners for every branch of the family.',
        tourUrl: tourLinks.floor2,
      },
      {
        id: 'third',
        label: 'Third Floor',
        plan: `${IMG}/floorplans/11br-third.webp`,
        blurb:
          'The top retreat — a game/media loft flanked by the final bedroom suites. The kids will claim it on night one.',
        tourUrl: tourLinks.floor3,
      },
    ] as RevealFloor[],
  },

  // Full-bleed parallax band — the photo stays put while content scrolls over
  // it, "peeking through" as the viewport moves past.
  parallaxBanner: {
    image: `${IMG}/lagoon-slide.webp`,
    eyebrow: 'Evermore Bay',
    headline: 'A 20-acre lagoon in our backyard',
    subhead:
      'Crystal water, a sand beach, cabanas, and a slide right outside the door — three days of this, together.',
    stat: { value: '20', unit: 'acres', label: 'of private beach lagoon' },
  },

  cost: {
    perPerson: '$390',
    perPersonNote: 'per adult (10+) · $260 per kid (3–9) · 2 & under free',
    // Numeric rates that drive the inline party calculator. Keep in sync with
    // the display strings above.
    rates: { adult: 390, kid: 260, baby: 0 },
    included: [
      '3 nights of lodging in private resort houses',
      'Access to the 20-acre Evermore Bay beach lagoon',
      'Private heated pools, game rooms, and resort amenities',
      'The Saturday family banquet — a dinner together',
      'A catered in-house family dinner night',
      'Shared keepsakes and family programming',
    ],
    timeline: 'To be determined.',
    reassurance: [
      'Your fee covers your room, a catered in-house dinner night, and the Saturday banquet — the big shared moments.',
      'Breakfasts and lunches are easy and on your own: every house has a full kitchen, plus a food hall, restaurants, and a market right on the resort.',
      'These are the memories you only get to make once.',
    ] as string[],
  },

  media: {
    // Resort walkthrough / guest review videos (YouTube video IDs).
    youTubeIds: [
      'LOODuti_RmI', // Evermore Resort 11-Bedroom House Review
      'LOkce7tE4Rc', // Evermore Resort Flats and Property Walk Review
      'uf8dtx27-GM', // Evermore Resort Property Walk Review
    ] as string[],
    // TODO: add the official resort site + review/things-to-do links.
    links: [] as RevealLink[],
    // On-property dining — no need to leave the resort for a meal.
    dining: {
      title: 'Dining, right on the resort',
      subtitle: 'No need to leave — every meal is steps from the lagoon.',
      options: [
        { name: 'Beach Bar & Grill', detail: 'Poolside bites and cocktails at the lagoon' },
        { name: 'The Food Hall', detail: 'Multiple counters — something for every appetite' },
        { name: 'Coffee & Café', detail: 'Morning espresso, pastries, and grab-and-go' },
        { name: 'Resort Market', detail: 'Groceries and essentials to stock the house kitchens' },
        { name: 'Lagoon-side Cabana Service', detail: 'Food and drinks brought right to your spot on the sand' },
      ],
    },
  },

  map: {
    center: { lat: 28.406001, lng: -81.526694 }, // Evermore Resort (Apple Maps place)
    zoom: 13,
    pins: [
      { lat: 28.406001, lng: -81.526694, label: 'Evermore Orlando Resort' },
    ] as RevealPin[],
    // Apple Maps place share link + the aerial used as the satellite-style card.
    appleMapsUrl:
      'https://maps.apple.com/place?map=satellite&place-id=I69FA78C823A8A530&address=1590+Evermore+Way%2C+Orlando%2C+FL++32836%2C+United+States&coordinate=28.406001%2C-81.526694&name=Evermore+Resort&_provider=9902',
    address: '1590 Evermore Way, Orlando, FL 32836',
    satelliteImage: `${IMG}/evermore-satellite.webp`,
    // Disney is ~5 min west of the resort; the overlay points that way.
    nearby: { label: 'Walt Disney World', detail: '5 minutes away' },
  },

  gallery: [
    { photo: `${IMG}/evermore_bay_entire_1920x1080.jpg.webp`, caption: 'Evermore Bay — the 20-acre beach lagoon' },
    { photo: `${IMG}/House_11bdr_slide.jpg.webp`, caption: 'Indoor slide inside the 11-bedroom house' },
    { photo: `${IMG}/primary_bathroom_house_1920x1080.jpg.webp`, caption: 'Spa-style primary bathroom' },
    { photo: `${IMG}/H62A2017-HDR-1copy.jpg.webp`, caption: 'Inside the resort residences' },
    { photo: `${IMG}/evermore_meeting_center_banquet_1920x1080.jpg.webp`, caption: 'Banquet space for the family dinner' },
    { photo: `${IMG}/meetings_corrected_-27.jpg.webp`, caption: 'Gathering & event space' },
  ] as RevealGalleryItem[],
} as const;

export type Reunion2027Content = typeof reunion2027;

export interface Reunion2027Cost {
  perPerson: string;
  perPersonNote: string;
  rates: { adult: number; kid: number; baby: number };
  depositLine: string;
  timeline: string;
}

// Derives ONLY the dynamic money/deposit/timeline strings from the reunion config.
// Falls back to the static reunion2027.cost copy when config values are absent (cents = 0).
export function getReunion2027Cost(reunion?: Reunion | null): Reunion2027Cost {
  const adultCents = reunion?.costPerAdult ?? 0;
  const childCents = reunion?.costPerChild ?? 0;
  const depositCents = reunion?.depositPerPerson ?? 0;
  const dueDate = reunion?.depositDueDate ?? null;

  // No configured pricing yet — preserve the existing static fallback copy.
  if (adultCents <= 0 && childCents <= 0) {
    return {
      perPerson: reunion2027.cost.perPerson,
      perPersonNote: reunion2027.cost.perPersonNote,
      rates: reunion2027.cost.rates,
      depositLine: depositCents > 0
        ? `Reserve your spot — ${formatCurrency(depositCents)}/person deposit due ${formatDate(dueDate)}`
        : '',
      timeline: reunion2027.cost.timeline,
    };
  }

  const adult = formatCurrency(adultCents);
  const child = formatCurrency(childCents);
  const deposit = formatCurrency(depositCents);

  return {
    perPerson: adult,
    perPersonNote: `per adult (10+) · ${child} per kid (3–9) · 2 & under free`,
    rates: { adult: adultCents / 100, kid: childCents / 100, baby: 0 },
    depositLine: depositCents > 0
      ? `Reserve your spot — ${deposit}/person deposit due ${formatDate(dueDate)}`
      : '',
    timeline: depositCents > 0
      ? `${deposit}/person due ${formatDate(dueDate)}; remaining balance collected over the following year.`
      : reunion2027.cost.timeline,
  };
}
